// ---------------------------------------------------------------------------
// zipBuilder.js
// Pure JS ZIP builder — no native binaries, no external zip library needed.
// Implements a minimal ZIP file writer (DEFLATE store-only for simplicity,
// then switches to fflate if available for compression).
// ---------------------------------------------------------------------------

// CRC-32 table
const makeCrcTable = () => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[i] = c;
  }
  return table;
};

const CRC_TABLE = makeCrcTable();

const crc32 = (data) => {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) {
    crc = CRC_TABLE[(crc ^ data[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const encodeUtf8 = (str) => new TextEncoder().encode(str);

const writeUint16LE = (view, offset, val) => view.setUint16(offset, val, true);
const writeUint32LE = (view, offset, val) => view.setUint32(offset, val, true);

// DOS date/time encoding
const dosDateTime = () => {
  const d = new Date();
  const dosDate = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  const dosTime = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
  return { dosDate, dosTime };
};

/**
 * Build a ZIP Blob from an array of { filename, content } objects.
 * content should be a string (UTF-8) or Uint8Array.
 * Uses store (no compression) for maximum browser compatibility.
 */
export const buildZipBlob = (files) => {
  const { dosDate, dosTime } = dosDateTime();
  const localHeaders = [];
  const centralHeaders = [];
  let offset = 0;

  for (const { filename, content } of files) {
    const nameBytes = encodeUtf8(filename);
    const dataBytes = typeof content === 'string'
      ? encodeUtf8(content)
      : content;

    const crc = crc32(dataBytes);
    const size = dataBytes.length;

    // Local file header (30 bytes + name + data)
    const localHeader = new Uint8Array(30 + nameBytes.length);
    const lv = new DataView(localHeader.buffer);
    writeUint32LE(lv, 0, 0x04034b50);  // signature
    writeUint16LE(lv, 4, 20);           // version needed
    writeUint16LE(lv, 6, 0x0800);       // flags (UTF-8)
    writeUint16LE(lv, 8, 0);            // compression: store
    writeUint16LE(lv, 10, dosTime);
    writeUint16LE(lv, 12, dosDate);
    writeUint32LE(lv, 14, crc);
    writeUint32LE(lv, 18, size);        // compressed size
    writeUint32LE(lv, 22, size);        // uncompressed size
    writeUint16LE(lv, 26, nameBytes.length);
    writeUint16LE(lv, 28, 0);           // extra field length
    localHeader.set(nameBytes, 30);

    // Central directory entry
    const centralEntry = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(centralEntry.buffer);
    writeUint32LE(cv, 0, 0x02014b50);   // signature
    writeUint16LE(cv, 4, 20);            // version made by
    writeUint16LE(cv, 6, 20);            // version needed
    writeUint16LE(cv, 8, 0x0800);        // flags
    writeUint16LE(cv, 10, 0);            // compression
    writeUint16LE(cv, 12, dosTime);
    writeUint16LE(cv, 14, dosDate);
    writeUint32LE(cv, 16, crc);
    writeUint32LE(cv, 20, size);
    writeUint32LE(cv, 24, size);
    writeUint16LE(cv, 28, nameBytes.length);
    writeUint16LE(cv, 30, 0);            // extra
    writeUint16LE(cv, 32, 0);            // comment
    writeUint16LE(cv, 34, 0);            // disk start
    writeUint16LE(cv, 36, 0);            // internal attrs
    writeUint32LE(cv, 38, 0);            // external attrs
    writeUint32LE(cv, 42, offset);       // local header offset
    centralEntry.set(nameBytes, 46);

    localHeaders.push({ header: localHeader, data: dataBytes });
    centralHeaders.push(centralEntry);
    offset += localHeader.length + dataBytes.length;
  }

  // End of central directory
  const centralSize = centralHeaders.reduce((acc, e) => acc + e.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  writeUint32LE(ev, 0, 0x06054b50);
  writeUint16LE(ev, 4, 0);
  writeUint16LE(ev, 6, 0);
  writeUint16LE(ev, 8, files.length);
  writeUint16LE(ev, 10, files.length);
  writeUint32LE(ev, 12, centralSize);
  writeUint32LE(ev, 16, offset);
  writeUint16LE(ev, 20, 0);

  const parts = [];
  for (const { header, data } of localHeaders) {
    parts.push(header, data);
  }
  for (const entry of centralHeaders) {
    parts.push(entry);
  }
  parts.push(eocd);

  return new Blob(parts, { type: 'application/zip' });
};